import styled from "styled-components";
import { atlasPalettes, THEME_MODES } from "../../theme";

const landingPaletteFor = (themeMode) => {
  const mode = themeMode === THEME_MODES.light ? THEME_MODES.light : THEME_MODES.dark;
  const palette = atlasPalettes[mode];

  if (mode === THEME_MODES.light) {
    return {
      ...palette,
      heroCopy: "#3f3f46",
      sectionBackground: "#fdfcf8",
      navOverlay: "rgba(255, 255, 255, 0.92)",
      ghostBorder: "#c7c7c2",
      ghostHoverBorder: "#8f8f89",
      inputBackground: "#ffffff",
      controlBackground: "#ffffff",
    };
  }

  return {
    ...palette,
    heroCopy: "#c9c9c9",
    sectionBackground: "#0c0c0c",
    navOverlay: "rgba(10, 10, 10, 0.92)",
    ghostBorder: "#3a3a3a",
    ghostHoverBorder: "#666666",
    inputBackground: "#000000",
    controlBackground: "transparent",
  };
};

const HomeWrapper = styled.div`
  --atlas-background: ${({ themeMode }) => landingPaletteFor(themeMode).background};
  --atlas-background-accent: ${({ themeMode }) => landingPaletteFor(themeMode).backgroundAccent};
  --atlas-foreground: ${({ themeMode }) => landingPaletteFor(themeMode).foreground};
  --atlas-muted-foreground: ${({ themeMode }) => landingPaletteFor(themeMode).textMuted};
  --atlas-border: ${({ themeMode }) => landingPaletteFor(themeMode).border};
  --atlas-brand: ${({ themeMode }) => landingPaletteFor(themeMode).brand};
  --atlas-brand-foreground: ${({ themeMode }) => landingPaletteFor(themeMode).brandForeground};
  --home-hero-copy: ${({ themeMode }) => landingPaletteFor(themeMode).heroCopy};
  --home-section-background: ${({ themeMode }) => landingPaletteFor(themeMode).sectionBackground};
  --home-nav-overlay: ${({ themeMode }) => landingPaletteFor(themeMode).navOverlay};
  --home-ghost-border: ${({ themeMode }) => landingPaletteFor(themeMode).ghostBorder};
  --home-ghost-hover-border: ${({ themeMode }) => landingPaletteFor(themeMode).ghostHoverBorder};
  --home-input-background: ${({ themeMode }) => landingPaletteFor(themeMode).inputBackground};
  --home-control-background: ${({ themeMode }) => landingPaletteFor(themeMode).controlBackground};

  background: var(--atlas-background);
  color: var(--atlas-foreground);
  font-family: var(--atlas-font-family);
  -webkit-font-smoothing: antialiased;
  line-height: 1.5;

  * {
    box-sizing: border-box;
  }

  a {
    color: inherit;
    text-decoration: none;
  }

  /* ── layout helpers ─────────────────────────────────────────── */
  .home-wrap {
    max-width: 1160px;
    margin: 0 auto;
    padding: 0 30px;
  }

  .home-btn {
    display: inline-block;
    background: var(--atlas-brand);
    color: #000;
    border: 0;
    border-radius: 3px;
    padding: 11px 18px;
    font-weight: 700;
    font-size: 13.5px;
    cursor: pointer;
    font-family: inherit;
    transition: opacity 0.15s ease;
    &:hover { opacity: 0.88; }
    &.ghost {
      background: transparent;
      border: 1px solid var(--home-ghost-border);
      color: var(--atlas-foreground);
      &:hover { border-color: var(--home-ghost-hover-border); }
    }
  }

  /* ── nav ────────────────────────────────────────────────────── */
  .home-nav {
    position: sticky;
    top: 0;
    z-index: 20;
    display: flex;
    align-items: center;
    gap: 26px;
    height: 80px;
    background: transparent;
    border-bottom: 1px solid transparent;
    padding: 0 30px;
    transition: background 0.2s ease, border-color 0.2s ease;

    &.scrolled {
      background: var(--home-nav-overlay);
      backdrop-filter: blur(8px);
      -webkit-backdrop-filter: blur(8px);
      border-bottom-color: var(--atlas-border);
    }
  }

  .home-nav-brand {
    display: flex;
    align-items: center;
    gap: 10px;
    font-weight: 700;
    font-size: 17px;
    white-space: nowrap;
  }

  .home-brand-logo {
    width: auto;
    height: 30px;
    display: block;
  }

  .home-nav-links {
    display: flex;
    gap: 22px;
    font-size: 13.5px;
    color: var(--atlas-muted-foreground);
    font-weight: 500;

    a {
      cursor: pointer;
      transition: color 0.15s;
      &:hover { color: var(--atlas-foreground); }
    }
  }

  .home-nav-sp { flex: 1; }

  .home-nav-controls {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .home-nav-lang {
    font-size: 12px;
    color: var(--atlas-muted-foreground);
    border: 1px solid var(--atlas-border);
    border-radius: 2px;
    overflow: hidden;
    display: flex;
    flex-shrink: 0;

    button {
      padding: 5px 8px;
      font-weight: 700;
      cursor: pointer;
      border: 0;
      background: transparent;
      color: inherit;
      font-family: inherit;
      transition: background 0.15s;
      &.on {
        background: var(--atlas-brand);
        color: #000;
        cursor: default;
      }
      &:not(.on):hover {
        background: var(--atlas-background-accent);
      }
    }
  }

  .home-nav-theme {
    width: 32px;
    height: 32px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 1px solid var(--atlas-border);
    border-radius: 2px;
    background: var(--home-control-background);
    color: var(--atlas-foreground);
    cursor: pointer;
    transition: background 0.15s ease, border-color 0.15s ease;

    &:hover {
      background: var(--atlas-background-accent);
      border-color: var(--home-ghost-hover-border);
    }
  }

  /* ── hero ───────────────────────────────────────────────────── */
  .home-hero {
    padding: 96px 0 84px;
  }

  .home-hero-bar {
    display: flex;
    gap: 26px;
  }

  .home-hero-accent {
    width: 5px;
    background: var(--atlas-brand);
    border-radius: 2px;
    flex: 0 0 5px;
    align-self: stretch;
    min-height: 180px;
  }

  .home-hero h1 {
    font-size: 64px;
    line-height: 1.08;
    margin: 0;
    font-weight: 700;
    letter-spacing: 0;

    .em { color: var(--atlas-brand); }
  }

  .home-hero p {
    color: var(--home-hero-copy);
    font-size: 19px;
    max-width: 640px;
    margin: 22px 0 28px;
    line-height: 1.5;
  }

  .home-cta {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
  }

  /* ── band ───────────────────────────────────────────────────── */
  .home-band {
    border-top: 1px solid var(--atlas-border);
    background: var(--home-section-background);
    padding: 64px 0;

    h2 {
      font-size: 44px;
      font-weight: 700;
      margin: 12px 0 12px;
      max-width: 20ch;
      line-height: 1.12;
    }
  }

  .home-band-sub {
    color: var(--atlas-muted-foreground);
    font-size: 16px;
    max-width: 680px;
    margin: 0;
  }

  /* ── capability rows ────────────────────────────────────────── */
  .home-feat {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 48px;
    align-items: center;
    padding: 64px 0;
    border-top: 1px solid var(--atlas-border);

    &.rev .home-feat-txt { order: 2; }

    h3 {
      font-size: 28px;
      font-weight: 700;
      margin: 10px 0 12px;
    }

    p {
      color: var(--atlas-muted-foreground);
      font-size: 15.5px;
      line-height: 1.6;
      margin: 0 0 20px;
    }
  }

  .home-vizbox {
    background: var(--atlas-background-accent);
    border: 1px solid var(--atlas-border);
    border-radius: 12px;
    height: 340px;
    overflow: hidden;
    display: flex;
    align-items: stretch;
    padding: 18px;
  }

  .vizbox-static {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    gap: 14px;
  }

  .vizbox-badges {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }

  .vizbox-pill {
    border: 1px solid var(--atlas-border);
    border-radius: 999px;
    padding: 5px 10px;
    font-size: 11px;
    font-family: var(--atlas-mono-font-family);
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--atlas-muted-foreground);
  }

  .vizbox-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
  }

  .vizbox-card {
    background: rgba(255, 255, 255, 0.03);
    border: 1px solid var(--atlas-border);
    border-radius: 10px;
    padding: 14px;
    display: flex;
    flex-direction: column;
    gap: 6px;

    strong {
      font-size: 24px;
      line-height: 1;
    }

    p {
      margin: 0;
      font-size: 13px;
      color: var(--atlas-muted-foreground);
      line-height: 1.45;
    }

    &.wide {
      gap: 10px;
      margin-top: auto;

      code {
        font-size: 12px;
        color: var(--atlas-foreground);
        font-family: var(--atlas-mono-font-family);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }
  }

  .vizbox-note {
    margin-top: auto;
    padding-top: 12px;
    border-top: 1px solid var(--atlas-border);
    font-size: 12px;
    color: var(--atlas-brand);
    font-family: var(--atlas-mono-font-family);
    letter-spacing: 0.04em;
  }

  .vizbox-list {
    display: grid;
    grid-template-columns: 1fr;
    gap: 10px;
  }

  .vizbox-list-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    border: 1px solid var(--atlas-border);
    border-radius: 10px;
    padding: 12px 14px;
    background: rgba(255, 255, 255, 0.03);

    span {
      color: var(--atlas-muted-foreground);
      font-size: 13px;
    }

    strong {
      font-size: 14px;
      font-family: var(--atlas-mono-font-family);
    }
  }

  .vizbox-label {
    font-size: 12px;
    color: var(--atlas-muted-foreground);
    font-family: var(--atlas-mono-font-family);
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  /* ── spotlight ──────────────────────────────────────────────── */
  .home-spot {
    border-top: 1px solid var(--atlas-border);
    background: var(--home-section-background);
    padding: 72px 0;
  }

  .home-spot-top {
    max-width: 720px;

    h2 {
      font-size: 34px;
      font-weight: 700;
      margin: 12px 0 14px;
    }
  }

  .home-spot-lede {
    color: var(--home-hero-copy);
    font-size: 17px;
    margin: 0;
  }

  .home-spot-grid {
    display: grid;
    grid-template-columns: 1.05fr 1fr;
    gap: 44px;
    align-items: center;
    margin-top: 40px;
  }

  .home-bullets {
    display: flex;
    flex-direction: column;
    gap: 22px;
    margin: 0 0 26px;
  }

  .home-bullet {
    h4 {
      font-size: 16px;
      font-weight: 700;
      margin: 0 0 4px;
      display: flex;
      align-items: center;
      gap: 9px;
    }

    p {
      color: var(--atlas-muted-foreground);
      font-size: 14px;
      margin: 0;
      padding-left: 17px;
    }
  }

  .home-bullet-dot {
    width: 8px;
    height: 8px;
    border-radius: 2px;
    background: var(--atlas-brand);
    flex-shrink: 0;
  }

  .home-spot-shot {
    background: var(--atlas-background-accent);
    border: 1px solid var(--atlas-border);
    border-radius: 14px;
    padding: 10px;
    height: 420px;
    overflow: hidden;
    display: flex;
    align-items: stretch;

    & > div,
    .atlas-widget-wrapper {
      width: 100%;
      flex: 1;
    }

    .atlas-widget {
      border: none !important;
      border-radius: 0 !important;
      background: transparent !important;
      height: 100%;
      display: flex;
      flex-direction: column;
    }
  }

  /* ── audiences ──────────────────────────────────────────────── */
  .home-aud {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 18px;
    padding: 64px 0;
    border-top: 1px solid var(--atlas-border);
  }

  .home-aud-card {
    background: var(--atlas-background-accent);
    border: 1px solid var(--atlas-border);
    border-radius: 12px;
    padding: 22px;

    h4 {
      font-size: 18px;
      margin: 6px 0 8px;
      font-weight: 700;
    }

    p {
      color: var(--atlas-muted-foreground);
      font-size: 14px;
      line-height: 1.55;
      margin: 0 0 12px;
    }

    a {
      color: var(--atlas-brand);
      font-weight: 700;
      font-size: 13.5px;
      cursor: pointer;
      &:hover { opacity: 0.8; }
    }
  }

  /* ── contact ────────────────────────────────────────────────── */
  .home-contact {
    border-top: 1px solid var(--atlas-border);
    background: var(--home-section-background);
    padding: 72px 0;

    h2 {
      font-size: 36px;
      font-weight: 700;
      margin: 12px 0 12px;
    }

    p {
      color: var(--atlas-muted-foreground);
      font-size: 15.5px;
      max-width: 42ch;
    }
  }

  .home-contact-grid {
    display: grid;
    grid-template-columns: 1fr 1.1fr;
    gap: 48px;
    align-items: start;
  }

  .home-form {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 14px;

    .full { grid-column: 1 / 3; }

    label {
      font-size: 12px;
      color: var(--atlas-muted-foreground);
      display: block;
      margin-bottom: 6px;
    }

    input,
    textarea {
      width: 100%;
      background: var(--home-input-background);
      border: 1px solid var(--atlas-border);
      border-radius: 4px;
      color: var(--atlas-foreground);
      padding: 11px 12px;
      font-family: var(--atlas-font-family);
      font-size: 14px;
      outline: none;
      transition: border-color 0.15s;

      &:focus { border-color: var(--atlas-brand); }
    }

    textarea {
      min-height: 92px;
      resize: vertical;
    }
  }

  .home-form-sent {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 200px;
    color: var(--atlas-brand);
    font-weight: 700;
    font-size: 16px;
    text-align: center;
    padding: 24px;
    border: 1px solid var(--atlas-border);
    border-radius: 8px;
  }

  /* ── footer ─────────────────────────────────────────────────── */
  .home-foot {
    border-top: 1px solid var(--atlas-border);
    padding: 40px 0 50px;
  }

  .home-foot-row {
    display: flex;
    align-items: center;
    gap: 18px;
    flex-wrap: wrap;
  }

  .home-foot-links {
    display: flex;
    gap: 18px;
    color: var(--atlas-muted-foreground);
    font-size: 13px;
    font-weight: 600;

    a {
      cursor: pointer;
      transition: color 0.15s;
      &:hover { color: var(--atlas-foreground); }
    }
  }

  .home-foot-cr {
    color: var(--atlas-muted-foreground);
    font-size: 12.5px;
    margin-top: 18px;
  }

  /* ── responsive ─────────────────────────────────────────────── */
  @media (max-width: 900px) {
    .home-nav-links { display: none; }
  }

  @media (max-width: 820px) {
    .home-hero h1 { font-size: 42px; }

    .home-feat,
    .home-spot-grid,
    .home-contact-grid,
    .home-aud {
      grid-template-columns: 1fr;
    }

    .home-feat.rev .home-feat-txt { order: 0; }

    .home-vizbox,
    .home-spot-shot { height: auto; }

    .vizbox-grid {
      grid-template-columns: 1fr;
    }
  }
`;

export default HomeWrapper;
